import { useState } from 'react'
import { Waypoint } from 'react-waypoint'

import { Layout, Table, Tooltip } from '@loadsmart/loadsmart-ui'

import { Icon } from 'core/ui/components/icon/Icon'

import { type Currency } from '../domain/Currency'
import { type DistanceUnit } from '../domain/DistanceUnit'
import { type Lane } from '../domain/Lane'
import { type Mode } from '../domain/Mode'
import {
  formatDistanceColumnName,
  formatRatePerDistanceColumnName,
  getEquipmentColumnName,
  getFormattedCurrency,
} from '../presenters/lanePresenter'
import { RfpLane } from './RfpLane'

export type LaneListProps = {
  lanes: Array<Lane>
  currency: Currency
  isBidDisabled: boolean
  shipperCompanyName: string
  rfpId: string
  displayResult?: boolean
  preferenceMode: Mode
  distanceUnit: DistanceUnit
}

function getLaneKey(lane: Lane) {
  /**
   * The "model" that we expect in the UI after using MultiMode
   * is to have the same lane, "duplicated" N times, one for each preference.
   * So, we need to differentiate them by the preference.id to avoid react key warnings.
   */
  return `${lane.id}-${lane.preferences?.[0].id}`
}

const BATCH_INCREMENT = 50

export function RfpLaneList({
  currency,
  lanes,
  isBidDisabled,
  rfpId,
  shipperCompanyName,
  preferenceMode,
  displayResult = false,
  distanceUnit,
}: LaneListProps) {
  const [visibleLanesCount, setVisibleLanesCount] = useState(BATCH_INCREMENT)
  const formattedCurrency = getFormattedCurrency(currency)

  return (
    <>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeadCell />
            <Table.HeadCell />
            <Table.HeadCell>Origin</Table.HeadCell>
            <Table.HeadCell>Destination </Table.HeadCell>
            <Table.HeadCell>ID</Table.HeadCell>
            <Table.HeadCell>{formatDistanceColumnName(distanceUnit)}</Table.HeadCell>
            <Table.HeadCell>Volume</Table.HeadCell>
            <Table.HeadCell>{getEquipmentColumnName(preferenceMode)}</Table.HeadCell>
            <Table.HeadCell>Flat rate ({formattedCurrency})</Table.HeadCell>
            <Table.HeadCell>
              {formatRatePerDistanceColumnName(distanceUnit, formattedCurrency)}
            </Table.HeadCell>
            <Table.HeadCell>Capacity</Table.HeadCell>
            {displayResult && (
              <Table.HeadCell>
                <Tooltip message="Variance from awarded rate">
                  <Layout.Group align="center" space="s">
                    Result
                    <Icon name="question-circle" />
                  </Layout.Group>
                </Tooltip>
              </Table.HeadCell>
            )}
          </Table.Row>
        </Table.Head>
        <Table.Body data-testid="table">
          {lanes.slice(0, visibleLanesCount).map((lane, index) => (
            <RfpLane
              index={index}
              currency={currency}
              lane={lane}
              key={getLaneKey(lane)}
              isBidDisabled={isBidDisabled}
              rfpId={rfpId}
              preferenceMode={preferenceMode}
              shipperCompanyName={shipperCompanyName}
              displayResult={displayResult}
              distanceUnit={distanceUnit}
            />
          ))}
        </Table.Body>
      </Table>
      <Waypoint
        onEnter={() => {
          /**
           * @TODO: Review this solution
           *
           * "Quick and naive" solution to avoid rendering lanes all lanes at once.
           * Having many lanes was causing the page to freeze in some cases.
           * So, we are rendering only the first 50 lanes and then incrementing 50 more each time,
           * levering React capacity to not render/commit again, based in the key, the same items.
           *
           * This will be refactored in future work to implement pagination.
           */
          if (visibleLanesCount < lanes.length) {
            setVisibleLanesCount(visibleLanesCount + BATCH_INCREMENT)
          }
        }}
      />
    </>
  )
}
